import { z } from 'zod';
import {
  buildDismissedGapsPayload,
  type DismissedGapPayload,
} from '@/features/hitl/planGapDismissals';

export const ASK_USER_CHOICE_TOOL_NAME = 'ask_user_choice';
export const PRESENT_PLAN_TOOL_NAME = 'present_plan';
export const OTHER_OPTION_VALUE = '__other__';

// --- Zod schemas for custom HITL tools ---

// Optional fields on these arg shapes use `.nullish()` (accepts null OR
// undefined) rather than just `.optional()` because the canonical JSON
// Schema in registry.ts is OpenAI strict-mode compliant — i.e., every
// declared property is in `required` and "optional" is modeled as a
// nullable type union (`["string","null"]`). The model emits `null` for
// absent values; we accept both null and undefined so historical thread
// payloads (pre-strict) still parse.
export const HitlChoiceOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
  description: z.string().nullish(),
});

export type HitlChoiceOption = z.infer<typeof HitlChoiceOptionSchema>;

export const AskUserChoiceArgsSchema = z.object({
  question: z.string(),
  options: z.array(HitlChoiceOptionSchema).min(1),
  title: z.string().nullish(),
  allowOther: z.boolean().nullish(),
  otherLabel: z.string().nullish(),
  allowSkip: z.boolean().nullish(),
  submitLabel: z.string().nullish(),
  multiSelect: z.boolean().nullish(),
  minSelections: z.number().nullish(),
  maxSelections: z.number().nullish(),
});

export type AskUserChoiceArgs = z.infer<typeof AskUserChoiceArgsSchema>;

export const PlanEntityConfidenceSchema = z.enum(['high', 'medium', 'low']);

// `source` is intentionally a free string rather than an enum: the planner's
// system-prompt currently uses 'verbal' / 'visual' but other corroboration
// labels may surface, and a Zod enum would silently drop entities when the
// model emits something off-list.
export const PlanEntitySchema = z.object({
  category: z.string(),
  label: z.string(),
  details: z.string().nullish(),
  confidence: PlanEntityConfidenceSchema,
  source: z.string(),
});

export const PlanGapTypeSchema = z.enum([
  'ambiguous',
  'missing',
  'multiple-options',
]);

export const PlanGapSchema = z.object({
  category: z.string(),
  description: z.string(),
  type: PlanGapTypeSchema,
  options: z.array(z.string()).nullish(),
});

export type PlanEntityConfidence = z.infer<typeof PlanEntityConfidenceSchema>;
export type PlanGapType = z.infer<typeof PlanGapTypeSchema>;
export type PlanEntity = z.infer<typeof PlanEntitySchema>;
export type PlanGap = z.infer<typeof PlanGapSchema>;

export const PresentPlanArgsSchema = z.object({
  plan: z.string(),
  scenario: z.enum(['generate-report', 'rewrite-narrative', 'entity-updates', 'error-fixes']),
  entityTypes: z.array(z.string()),
  summary: z.string().nullish(),
  technical_plan: z.string().nullish(),
  entities: z.array(PlanEntitySchema).nullish(),
  gaps: z.array(PlanGapSchema).nullish(),
});

export type PresentPlanArgs = z.infer<typeof PresentPlanArgsSchema>;

export interface PlanApprovalDraft {
  decision: 'pending' | 'approved' | 'feedback';
  feedbackText: string;
  /**
   * Open questions the user struck out — "doesn't apply here". Sent as
   * `dismissedGaps` on either resume path; the server records them on the
   * thread so the agent can't raise them again. Optional so a draft built
   * before any dismissal still type-checks as-is.
   */
  dismissedGaps?: DismissedGapPayload[];
}

export const HitlToolDefinitionSchema = z.object({
  name: z.string(),
  description: z.string(),
  schema: z.record(z.string(), z.unknown()),
  agentNames: z.array(z.string()).optional(),
});

export type HitlToolDefinition = z.infer<typeof HitlToolDefinitionSchema>;

export const HitlToolRegistrySchema = z.record(z.string(), HitlToolDefinitionSchema);
export type HitlToolRegistry = z.infer<typeof HitlToolRegistrySchema>;

// --- Zod schemas for official humanInTheLoopMiddleware approval interrupts ---

export const ApprovalActionRequestSchema = z.object({
  name: z.string(),
  arguments: z.record(z.string(), z.unknown()),
  description: z.string().optional(),
});

export const ApprovalReviewConfigSchema = z.object({
  actionName: z.string(),
  allowedDecisions: z.array(z.string()),
});

export const ApprovalInterruptPayloadSchema = z.object({
  actionRequests: z.array(ApprovalActionRequestSchema),
  reviewConfigs: z.array(ApprovalReviewConfigSchema),
});

export type ApprovalInterruptPayload = z.infer<typeof ApprovalInterruptPayloadSchema>;

export const ApprovalDecisionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('approve') }),
  z.object({
    type: z.literal('edit'),
    editedAction: z.object({
      name: z.string(),
      args: z.record(z.string(), z.unknown()),
    }),
  }),
  z.object({ type: z.literal('reject'), message: z.string().optional() }),
]);

export type ApprovalDecision = z.infer<typeof ApprovalDecisionSchema>;

// --- Interrupt payload types ---

export interface HitlInterruptPayload {
  toolName: string;
  toolCallId?: string;
  toolArgs?: unknown;
  schema?: Record<string, unknown>;
}

export interface PendingHitlInterrupt {
  interruptId: string;
  threadId?: string;
  namespace?: string[];
  toolName: string;
  toolCallId?: string;
  toolArgs?: unknown;
  schema?: Record<string, unknown>;
  ts: string;
  turn?: number;
  requestId?: string;
  agent?: string;
  source: 'stream' | 'history';
}

export interface PendingApprovalInterrupt {
  interruptId: string;
  threadId?: string;
  namespace?: string[];
  payload: ApprovalInterruptPayload;
  ts: string;
  turn?: number;
  source: 'stream' | 'history';
}

export interface AskUserChoiceDraft {
  selectedValues: string[];
  otherText: string;
  skipped: boolean;
}

export type HitlAnswerDraft =
  | {
      mode: 'ask_user_choice';
      value: AskUserChoiceDraft;
    }
  | {
      mode: 'present_plan';
      value: PlanApprovalDraft;
    }
  | {
      mode: 'generic';
      value: unknown;
    };

export interface HitlResumeResponse {
  interruptId: string;
  userInput: unknown;
}

export type HitlBuildResult =
  | {
      ok: true;
      userInput: unknown;
    }
  | {
      ok: false;
      error: string;
    };

export const parseAskUserChoiceArgs = (value: unknown): AskUserChoiceArgs | null => {
  const result = AskUserChoiceArgsSchema.safeParse(value);
  return result.success ? result.data : null;
};

export const parseApprovalInterruptPayload = (value: unknown): ApprovalInterruptPayload | null => {
  const result = ApprovalInterruptPayloadSchema.safeParse(value);
  return result.success ? result.data : null;
};

export const parsePresentPlanArgs = (value: unknown): PresentPlanArgs | null => {
  const result = PresentPlanArgsSchema.safeParse(value);
  return result.success ? result.data : null;
};

export const createDraftForInterrupt = (interrupt: PendingHitlInterrupt): HitlAnswerDraft => {
  if (interrupt.toolName === PRESENT_PLAN_TOOL_NAME && parsePresentPlanArgs(interrupt.toolArgs)) {
    return {
      mode: 'present_plan',
      value: {
        decision: 'pending',
        feedbackText: '',
      },
    };
  }

  const parsedArgs = parseAskUserChoiceArgs(interrupt.toolArgs);

  if (interrupt.toolName === ASK_USER_CHOICE_TOOL_NAME && parsedArgs) {
    return {
      mode: 'ask_user_choice',
      value: {
        selectedValues: [],
        otherText: '',
        skipped: false,
      },
    };
  }

  return {
    mode: 'generic',
    value: {},
  };
};

export const getPlanDraft = (
  interrupt: PendingHitlInterrupt,
  draft: HitlAnswerDraft | undefined
): PlanApprovalDraft => {
  if (draft?.mode === 'present_plan') {
    return draft.value;
  }

  const fallback = createDraftForInterrupt(interrupt);
  if (fallback.mode === 'present_plan') {
    return fallback.value;
  }

  return {
    decision: 'pending',
    feedbackText: '',
  };
};

const getChoiceValidationRange = (args: AskUserChoiceArgs) => {
  const minSelections = typeof args.minSelections === 'number'
    ? args.minSelections
    : 1;

  const maxSelections = typeof args.maxSelections === 'number'
    ? args.maxSelections
    : args.multiSelect
      ? Number.POSITIVE_INFINITY
      : 1;

  return {
    minSelections,
    maxSelections,
  };
};

export const buildHitlUserInput = (
  interrupt: PendingHitlInterrupt,
  draft: HitlAnswerDraft
): HitlBuildResult => {
  if (interrupt.toolName === PRESENT_PLAN_TOOL_NAME) {
    if (draft.mode !== 'present_plan') {
      return { ok: false, error: 'Invalid draft mode for plan approval.' };
    }
    // Capped and normalized here rather than trusting the server's own
    // truncation: it applies the caps when it reads the field, but persists
    // the payload as sent.
    const dismissedGaps = buildDismissedGapsPayload(draft.value.dismissedGaps ?? []);
    const dismissal = dismissedGaps.length > 0 ? { dismissedGaps } : {};

    if (draft.value.decision === 'approved') {
      return { ok: true, userInput: { approved: true, ...dismissal } };
    }
    if (draft.value.decision === 'feedback') {
      const feedback = draft.value.feedbackText.trim();
      if (!feedback) {
        return { ok: false, error: 'Please provide feedback before submitting.' };
      }
      return { ok: true, userInput: { approved: false, feedback, ...dismissal } };
    }
    return { ok: false, error: 'Please approve the plan or provide feedback.' };
  }

  if (interrupt.toolName === ASK_USER_CHOICE_TOOL_NAME) {
    const args = parseAskUserChoiceArgs(interrupt.toolArgs);

    if (!args) {
      return {
        ok: false,
        error: 'The tool arguments for this question are invalid.',
      };
    }

    if (draft.mode !== 'ask_user_choice') {
      return {
        ok: false,
        error: 'The answer format for this question is invalid.',
      };
    }

    if (draft.value.skipped) {
      if (!args.allowSkip) {
        return {
          ok: false,
          error: 'Skipping is not allowed for this question.',
        };
      }

      return {
        ok: true,
        userInput: {
          skipped: true,
        },
      };
    }

    const uniqueSelectedValues = Array.from(new Set(draft.value.selectedValues));
    const hasOther = uniqueSelectedValues.includes(OTHER_OPTION_VALUE);
    const selectedValues = uniqueSelectedValues.filter((value) => value !== OTHER_OPTION_VALUE);

    if (hasOther && !args.allowOther) {
      return {
        ok: false,
        error: 'This question does not allow an "Other" answer.',
      };
    }

    const trimmedOtherText = draft.value.otherText.trim();

    if (hasOther && !trimmedOtherText) {
      return {
        ok: false,
        error: 'Please provide the "Other" answer text before submitting.',
      };
    }

    const answeredCount = selectedValues.length + (hasOther ? 1 : 0);

    const { minSelections, maxSelections } = getChoiceValidationRange(args);

    if (answeredCount < minSelections) {
      return {
        ok: false,
        error: minSelections === 1
          ? 'Please select at least one option.'
          : `Please select at least ${minSelections} options.`,
      };
    }

    if (answeredCount > maxSelections) {
      return {
        ok: false,
        error: Number.isFinite(maxSelections)
          ? `Please select no more than ${maxSelections} options.`
          : 'Too many options were selected.',
      };
    }

    if (!args.multiSelect && answeredCount > 1) {
      return {
        ok: false,
        error: 'Please select only one option for this question.',
      };
    }

    const normalizedSelectedValues = [
      ...selectedValues,
      ...(hasOther ? ['other'] : []),
    ];

    const selectedValue = !args.multiSelect
      ? normalizedSelectedValues[0] ?? null
      : undefined;

    return {
      ok: true,
      userInput: {
        selectedValues: normalizedSelectedValues,
        ...(typeof selectedValue === 'string' ? { selectedValue } : {}),
        ...(hasOther ? { otherText: trimmedOtherText } : {}),
      },
    };
  }

  if (draft.mode !== 'generic') {
    return {
      ok: false,
      error: 'The answer format for this question is invalid.',
    };
  }

  return {
    ok: true,
    userInput: draft.value,
  };
};
